// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

import {
    useEffect,
    useState,
} from "react";

import {
    financeText,
    getFinanceLanguage,
} from "finance-language";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavItem
import {
    item,
    itemContent,
    itemArrow,
} from "examples/Sidenav/styles/sidenavItem";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

function SidenavItem({
                         color = "info",
                         name,
                         active = false,
                         nested = false,
                         children = false,
                         open = false,
                         icon = null,
                         openUp = false,
                         ...rest
                     }) {
    const [controller] =
        useMaterialUIController();

    const {
        miniSidenav,
        transparentSidenav,
        whiteSidenav,
        darkMode,
    } = controller;

    const [
        financeLanguage,
        setFinanceLanguage,
    ] = useState(
        getFinanceLanguage()
    );

    useEffect(() => {
        const handleLanguageChange = (
            event
        ) => {
            setFinanceLanguage(
                event?.detail === "tr"
                    ? "tr"
                    : "en"
            );
        };

        window.addEventListener(
            "finance-language-change",
            handleLanguageChange
        );

        return () => {
            window.removeEventListener(
                "finance-language-change",
                handleLanguageChange
            );
        };
    }, []);

    const useChildStyle =
        nested || children;

    return (
        <>
            <ListItem
                {...rest}
                component="li"
                sx={(theme) =>
                    item(
                        theme,
                        {
                            active,
                            color,
                            transparentSidenav,
                            whiteSidenav,
                            darkMode,
                        }
                    )
                }
            >
                <MDBox
                    role="button"
                    tabIndex={0}
                    sx={(theme) => ({
                        ...itemContent(
                            theme,
                            {
                                active,
                                miniSidenav,
                                nested,
                                children,
                                open,
                                transparentSidenav,
                                whiteSidenav,
                                darkMode,
                            }
                        ),

                        ...(useChildStyle && {
                            display:
                                "inline-flex",
                            width:
                                "fit-content",
                            minWidth:
                                "unset",
                            maxWidth:
                                "100%",

                            paddingLeft:
                                "12px",
                            paddingRight:
                                "12px",

                            marginLeft:
                                "8px",

                            borderRadius:
                                "10px",

                            transition:
                                "all 0.2s ease",

                            ...(active && {
                                backgroundColor:
                                    "#1976d2 !important",
                                background:
                                    "#1976d2 !important",
                                boxShadow:
                                    "none !important",

                                "& .MuiTypography-root":
                                    {
                                        color:
                                            "#fff !important",
                                        fontSize:
                                            "0.875rem !important",
                                        fontWeight:
                                            "400 !important",
                                        lineHeight:
                                            "1.5 !important",
                                    },

                                "& .MuiListItemText-primary":
                                    {
                                        color:
                                            "#fff !important",
                                        fontSize:
                                            "0.875rem !important",
                                        fontWeight:
                                            "400 !important",
                                        lineHeight:
                                            "1.5 !important",
                                    },
                            }),
                        }),
                    })}
                >
                    {icon && (
                        <MDBox
                            className="sidenav-icon-box"
                            component="span"
                        >
                            {icon}
                        </MDBox>
                    )}

                    <ListItemText
                        primary={financeText(
                            name,
                            financeLanguage
                        )}
                        primaryTypographyProps={{
                            fontSize:
                                "0.875rem",
                            fontWeight: 400,
                        }}
                    />

                    {children && (
                        <Icon
                            component="i"
                            sx={(theme) =>
                                itemArrow(
                                    theme,
                                    {
                                        open,
                                        miniSidenav,
                                        transparentSidenav,
                                        whiteSidenav,
                                        darkMode,
                                    }
                                )
                            }
                        >
                            expand_less
                        </Icon>
                    )}
                </MDBox>
            </ListItem>

            {children && (
                <Collapse
                    in={open}
                    timeout={250}
                    unmountOnExit
                >
                    <MDBox
                        sx={{
                            mt: openUp
                                ? 0
                                : 0.5,
                            mb: openUp
                                ? 0.8
                                : 0.8,
                            ml: 2,
                            pl: 1.5,
                            borderLeft:
                                "1px solid rgba(255,255,255,0.12)",

                            ...(openUp && {
                                display:
                                    "flex",
                                flexDirection:
                                    "column-reverse",
                            }),
                        }}
                    >
                        {children}
                    </MDBox>
                </Collapse>
            )}
        </>
    );
}

// Typechecking props for the SidenavItem
SidenavItem.propTypes = {
    color: PropTypes.oneOf([
        "primary",
        "secondary",
        "info",
        "success",
        "warning",
        "error",
        "dark",
    ]),

    name: PropTypes.string.isRequired,

    active: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string,
    ]),

    nested: PropTypes.bool,

    children: PropTypes.node,

    open: PropTypes.bool,

    icon: PropTypes.node,

    openUp: PropTypes.bool,
};

export default SidenavItem;